import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, ChevronUp, Search, SlidersHorizontal, Sparkles, Star, X } from 'lucide-react';
import styles from '../Feedback/ReviewsHolder.module.css';
import EachFeedbackBox from './EachFeedbackBox';

const ReviewsHolder = props => {
    const { isLoading, feedbackData } = props;

    const [selectedStar, setSelectedStar] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [isSalonEnvironment, setIsSalonEnvironment] = useState(false);
    const [isServiceResult, setIsServiceResult] = useState(false);
    const [isStaffBehavior, setIsStaffBehavior] = useState(false);
    const [selectedInterval, setSelectedInterval] = useState('');
    const [searchKey, setSearchKey] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    const filtersHandler = () => {
        let temp = Array.isArray(feedbackData) ? [...feedbackData] : [];
        if (selectedStar !== '') {
            temp = temp.filter((item) => item.selectedStar === selectedStar);
        }

        const keyword = searchKey.trim().toLowerCase();
        if (keyword) {
            temp = temp.filter(item =>
                (item?.clientName || '').toLowerCase().includes(keyword) ||
                (item?.careToShareALilMoreTxt || '').toLowerCase().includes(keyword) ||
                (item?.reviewIdentifier || '').toLowerCase().includes(keyword)
            );
        }

        if (isSalonEnvironment) {
            temp = temp.filter((item) => item.starSelectedSalonEnvironment !== '0');
        }

        if (isServiceResult) {
            temp = temp.filter((item) => item.starSelectedServiceResults !== '0');
        }

        if (isStaffBehavior) {
            temp = temp.filter((item) => item.starSelectedStaffBehaviour !== '0');
        }

        const reviewCountByClient = temp.reduce((acc, review) => {
            const key = review?.clientId || review?.clientName || 'UNKNOWN';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});

        temp = temp.filter(review => {
            const key = review?.clientId || review?.clientName || 'UNKNOWN';
            const count = reviewCountByClient[key] || 0;
            switch (selectedInterval) {
                case 'Clients with 1 review':
                    return count === 1;
                case 'Clients with more than 1 review':
                    return count > 1;
                case 'Clients with more than 2 reviews':
                    return count > 2;
                case 'Clients with 5 reviews':
                    return count === 5;
                case 'Clients with more than 5 reviews':
                    return count > 5;
                default:
                    return true;
            }
        });

        setFilteredData(temp);
    };

    useEffect(() => {
        filtersHandler();
    }, [selectedStar, isSalonEnvironment, isServiceResult, isStaffBehavior, feedbackData, selectedInterval, searchKey]);

    const intervals = [
        'Clients with 1 review',
        'Clients with more than 1 review',
        'Clients with more than 2 reviews',
        'Clients with 5 reviews',
        'Clients with more than 5 reviews',
    ];

    const hasActiveFilters =
        selectedStar !== '' ||
        selectedInterval !== '' ||
        isSalonEnvironment ||
        isServiceResult ||
        isStaffBehavior ||
        searchKey.trim() !== '';

    const activeAdvancedCount =
        (selectedInterval !== '' ? 1 : 0) +
        (isSalonEnvironment ? 1 : 0) +
        (isServiceResult ? 1 : 0) +
        (isStaffBehavior ? 1 : 0);

    const clearFilters = () => {
        setSelectedStar('');
        setSelectedInterval('');
        setIsSalonEnvironment(false);
        setIsServiceResult(false);
        setIsStaffBehavior(false);
        setSearchKey('');
    };

    return (
        <motion.div className={styles.reviewsHolderWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.38 }}>
            <div className={styles.rwTopBar}>
                <div className={styles.rwTitleBlock}>
                    <span className={styles.rwTitle}>Reviews Inbox</span>
                    <span className={styles.rwSubtitle}>Filter and scan client feedback quickly</span>
                </div>
                <div className={styles.reviewCount}>
                    <Sparkles size={13} /> {filteredData.length} reviews
                </div>
            </div>

            <motion.div className={styles.rwHeader} initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.28 }}>
                <div className={styles.searchControlRow}>
                    <div className={styles.searchWrap}>
                        <Search size={14} className={styles.searchIcon} />
                        <input
                            className={styles.searchInput}
                            placeholder="Search client or comment..."
                            value={searchKey}
                            onChange={(e) => setSearchKey(e.target.value)}
                        />
                        {searchKey && (
                            <button className={styles.clearSearchBtn} onClick={() => setSearchKey('')}>
                                <X size={12} />
                            </button>
                        )}
                    </div>

                    <div className={styles.filterActions} >
                        <div className={`${styles.selectWrapper} ${styles.selectCompact}`}>
                            <Star size={12} className={styles.selectIcon} />
                            <select onChange={(e) => setSelectedStar(e.target.value)} value={selectedStar} className={styles.selectBox}>
                                <option value="">All Stars</option>
                                <option value="5">5 Stars</option>
                                <option value="4">4 Stars</option>
                                <option value="3">3 Stars</option>
                                <option value="2">2 Stars</option>
                                <option value="1">1 Star</option>
                            </select>
                        </div>

                        <div className={styles.primaryActions}>
                            <button
                                className={`${styles.advancedToggleBtn} ${showAdvancedFilters ? styles.advancedToggleBtnActive : ''}`}
                                onClick={() => setShowAdvancedFilters(prev => !prev)}
                            >
                                <SlidersHorizontal size={13} />
                                Advanced Filters
                                {activeAdvancedCount > 0 && <span className={styles.advancedCount}>{activeAdvancedCount}</span>}
                                {showAdvancedFilters ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            </button>

                            {hasActiveFilters && (
                                <button onClick={clearFilters} className={styles.resetBtn}>
                                    <X size={12} /> Reset
                                </button>
                            )}
                        </div>
                    </div>

                </div>

                {showAdvancedFilters && (
                    <motion.div
                        className={styles.advancedPanel}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.18 }}
                    >
                        <div className={styles.controlRow}>
                            <div className={styles.selectWrapper}>
                                <SlidersHorizontal size={12} className={styles.selectIcon} />
                                <select value={selectedInterval} onChange={e => setSelectedInterval(e.target.value)} className={styles.selectBox}>
                                    <option value="">All Review Frequency</option>
                                    {intervals.map(interval => (
                                        <option key={interval} value={interval}>{interval}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.optionWrapper}>
                            <button
                                onClick={() => setIsSalonEnvironment(!isSalonEnvironment)}
                                className={`${styles.toggleBtn} ${isSalonEnvironment ? styles.toggleBtnActive : ''}`}
                            >
                                <CheckCircle2 size={12} /> Salon Environment
                            </button>

                            <button
                                onClick={() => setIsServiceResult(!isServiceResult)}
                                className={`${styles.toggleBtn} ${isServiceResult ? styles.toggleBtnActive : ''}`}
                            >
                                <CheckCircle2 size={12} /> Service Results
                            </button>

                            <button
                                onClick={() => setIsStaffBehavior(!isStaffBehavior)}
                                className={`${styles.toggleBtn} ${isStaffBehavior ? styles.toggleBtnActive : ''}`}
                            >
                                <CheckCircle2 size={12} /> Staff Behaviour
                            </button>
                        </div>
                    </motion.div>
                )}
            </motion.div>

            <motion.div className={styles.rwBody} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.34 }}>
                {isLoading === false ? (
                    filteredData?.length > 0 ? (
                        <div className={styles.reviewList}>
                            {filteredData?.map((item, index) => (
                                <EachFeedbackBox key={item?.reviewIdentifier || index} item={item} />
                            ))}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <span className={styles.emptyTitle}>No reviews match your filters</span>
                            <span className={styles.emptySub}>Try adjusting star, frequency, or factor filters.</span>
                        </div>
                    )
                ) : (
                    <div className={styles.loadingWrapper}>Loading reviews...</div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default ReviewsHolder;